import {
    ButtonSecondary,
    Colors,
    FOCUS_RING,
    formatDate,
    Icon,
    ModalDialog,
    Pagination,
    Text,
} from '@amelio/ui-library';
import { useEffect, useState } from 'react';

import { LaunchedSurveysModal } from './LaunchedSurveysModal';
import { ManagerLaunchStatusModal } from './ManagerLaunchStatusModal';

import type { TranslationKey } from '@/i18n-key';
import type { SentCycle } from '@/types/cycle-evaluation/SentCycle';

import { useGetSentCyclesPaged } from '@/api/cycle-evaluation-services/sent-cycles';
import { Bone, Skeleton } from '@/components/Skeleton/Skeleton';
import {
    EvaluationType,
    type EvaluationTypeValue,
} from '@/constants/cycle-evaluation/CycleEvaluation';
import { useAmelioTranslations } from '@/hooks';
import { useReplaceDynamicTags } from '@/hooks/useReplaceDynamicTags';
import {
    getTypeBadgeLabelKey,
    type EvaluationTemplateSummary,
} from '@/pages/CycleEvaluation/Dashboard/cycleListRows';
import i18n from '@/plugins/i18n';
import { getLocalizedText } from '@/utils/localization';

type Props = {
    isOpen: boolean;
    cycleEvaluationId: string;
    /** The cycle name titles the popup: it is the identity of what people were invited to. */
    cycleName: string;
    /**
     * The templates behind each evaluation type of the cycle, shown under their own label so an
     * administrator can tell which questionnaire went out. Empty for a cycle created before the
     * redesign, whose template name already stands in as its title.
     */
    evaluationTemplates?: EvaluationTemplateSummary[];
    reviewType: EvaluationTypeValue;
    onClose: () => void;
};

type SentCyclesContentProps = {
    isLoading: boolean;
    sentCycles: SentCycle[];
    handleLaunchedSurveysClick: (sentCyleId: string) => void;
    handleManagerLaunchRateClick: (sentCycleId: string) => void;
};

type NestedDialog = 'launchedSurveys' | 'managerLaunchStatus';

const TABLE_COLUMNS = 'grid grid-cols-[1.2fr_1fr_1.2fr_1fr_1.6fr] items-center gap-4';
const PAGE_SIZE = 5;

const SentCyclesContent = ({
    isLoading,
    sentCycles,
    handleLaunchedSurveysClick,
    handleManagerLaunchRateClick,
}: SentCyclesContentProps) => {
    const { t } = useAmelioTranslations();
    if (isLoading) {
        return (
            <Skeleton className={'bg-base-50 flex flex-col gap-2'}>
                {Array.from({ length: 5 }, (_, index) => (
                    <Bone
                        key={index}
                        className={'bg-base h-16 w-full rounded-lg'}
                    />
                ))}
            </Skeleton>
        );
    }

    if (sentCycles.length === 0) {
        return (
            <div
                className={'py-6 text-center'}
                data-testid={'sent-cycles-empty-state'}
            >
                <Text className={'text-center'}>
                    {t('cycleEvaluationDashboard.noResultsFound')}
                </Text>
            </div>
        );
    }

    return (
        <>
            {sentCycles.map((sentCycle) => (
                <div
                    key={sentCycle.id}
                    className={`${TABLE_COLUMNS} border-base-100 border-b px-4 py-4`}
                    data-testid={`sent-cycle-row-${sentCycle.id}`}
                >
                    <Text size={'small'}>
                        {formatDate({
                            date: sentCycle.launchDate,
                            language: i18n.language,
                        })}
                    </Text>

                    <Text
                        size={'small'}
                        className={'text-center'}
                    >
                        {sentCycle.peopleEvaluated}
                    </Text>

                    <button
                        type={'button'}
                        className={`${FOCUS_RING} w-full cursor-pointer hover:underline`}
                        onClick={() => handleManagerLaunchRateClick(sentCycle.id)}
                        data-testid={`manager-launch-rate-${sentCycle.id}`}
                    >
                        <Text
                            as={'span'}
                            size={'small'}
                            weight={'bold'}
                            color={Colors.SECONDARY_800}
                            className={'text-center'}
                        >
                            {sentCycle.managerLaunchRate}%
                        </Text>
                    </button>

                    <Text
                        size={'small'}
                        className={'text-center'}
                    >
                        {sentCycle.completionRate}%
                    </Text>

                    <div className={'flex items-center justify-end gap-2'}>
                        <ButtonSecondary
                            size={'sm'}
                            dataTestId={`see-launched-surveys-${sentCycle.id}`}
                            onClick={() => handleLaunchedSurveysClick(sentCycle.id)}
                        >
                            <span className={'flex items-center gap-2'}>
                                {t('cycleEvaluationDashboard.sentCycles.seeLaunchedSurveys')}

                                <Icon
                                    name={'arrow-arrow-right-1'}
                                    size={16}
                                />
                            </span>
                        </ButtonSecondary>
                    </div>
                </div>
            ))}
        </>
    );
};

const getSentCyclesSubtitleKey = (reviewType: EvaluationTypeValue): TranslationKey => {
    switch (reviewType) {
        case EvaluationType.SELF_EVALUATION:
            return 'cycleEvaluationDashboard.sentCycles.subtitleSelf';
        case EvaluationType.MANAGER_EVALUATION:
            return 'cycleEvaluationDashboard.sentCycles.subtitleManager';
        case EvaluationType.EVALUATION_360:
        default:
            return 'cycleEvaluationDashboard.sentCycles.subtitle360';
    }
};

export const SentCyclesList = ({
    isOpen,
    cycleEvaluationId,
    cycleName,
    evaluationTemplates = [],
    reviewType,
    onClose,
}: Props) => {
    const { t } = useAmelioTranslations();
    const replaceDynamicTags = useReplaceDynamicTags();
    const [currentPage, setCurrentPage] = useState(1);
    const [openNestedDialog, setOpenNestedDialog] = useState<NestedDialog | undefined>(undefined);
    // Kept after a nested dialog closes so its content stays in place while the dialog fades out.
    const [nestedSentCycleId, setNestedSentCycleId] = useState('');
    const templateLines = evaluationTemplates
        .map(({ reviewType: templateReviewType, title }) => ({
            reviewType: templateReviewType,
            name: replaceDynamicTags({ text: getLocalizedText(title) }),
        }))
        .filter(({ name }) => name.length > 0);

    const { data: sentCyclesData, isLoading } = useGetSentCyclesPaged({
        cycleEvaluationId,
        pageNumber: currentPage,
        pageSize: PAGE_SIZE,
    });

    const sentCycles = sentCyclesData?.items ?? [];
    const totalCount = sentCyclesData?.totalCount ?? 0;
    const totalPages = Math.ceil(totalCount / PAGE_SIZE);

    useEffect(() => {
        setCurrentPage(1);
    }, [cycleEvaluationId]);

    const openNested = (dialog: NestedDialog, sentCycleId: string) => {
        setNestedSentCycleId(sentCycleId);
        setOpenNestedDialog(dialog);
    };

    const closeNested = () => setOpenNestedDialog(undefined);

    // The nested dialogs are siblings of this one, never its children: React bubbles keyboard
    // events through portals, so a child dialog would hand every Tab to this dialog's focus trap.
    // They are declared before it: when both close together (Escape), a nested dialog releases its
    // page scroll lock first, so this dialog's release restores the page's own scrolling instead of
    // the lock the nested dialog had captured.
    return (
        <>
            <LaunchedSurveysModal
                isOpen={openNestedDialog === 'launchedSurveys'}
                sentCycleId={nestedSentCycleId}
                cycleName={cycleName}
                onClose={closeNested}
            />

            <ManagerLaunchStatusModal
                isOpen={openNestedDialog === 'managerLaunchStatus'}
                sentCycleId={nestedSentCycleId}
                onClose={closeNested}
            />

            <ModalDialog
                isOpen={isOpen}
                onClose={onClose}
                title={t('cycleEvaluationDashboard.sentCycles.title', {
                    cycleName,
                })}
                titleIcon={
                    <Icon
                        name={'business-chart'}
                        color={Colors.PRIMARY}
                        ariaHidden
                    />
                }
                subtitle={t(getSentCyclesSubtitleKey(reviewType))}
                size={'lg'}
                closeAriaLabel={t('common.close')}
                backdropBlur
                dataTestId={'sent-cycles-modal'}
                footer={
                    <div data-testid={'sent-cycles-footer-note'}>
                        <Text
                            size={'small'}
                            color={Colors.BASE_600}
                            className={'italic'}
                        >
                            {t('cycleEvaluationDashboard.sentCycles.footerNote')}
                        </Text>
                    </div>
                }
            >
                <div data-testid={'sent-cycles-list'}>
                    {/* Which evaluation template each type of this cycle was launched with: the list is
                        otherwise silent about what was actually sent. */}
                    {templateLines.length > 0 && (
                        <div
                            className={'mb-3 flex flex-col gap-1'}
                            data-testid={'sent-cycles-evaluation-templates'}
                        >
                            <Text
                                size={'small'}
                                weight={'bold'}
                                color={Colors.BASE_600}
                            >
                                {t('cycleEvaluationDashboard.evaluationTemplateLabel')}
                            </Text>

                            {templateLines.map(({ reviewType: templateReviewType, name }) => (
                                <div
                                    key={templateReviewType}
                                    className={'flex flex-wrap items-baseline gap-1'}
                                    data-testid={`sent-cycles-evaluation-template-${templateReviewType}`}
                                >
                                    <Text
                                        size={'small'}
                                        color={Colors.BASE_600}
                                    >
                                        {t(getTypeBadgeLabelKey(templateReviewType))}
                                    </Text>

                                    <Text
                                        size={'small'}
                                        color={Colors.BASE_600}
                                    >
                                        {`· ${name}`}
                                    </Text>
                                </div>
                            ))}
                        </div>
                    )}

                    <div
                        className={`${TABLE_COLUMNS} bg-base-50 mb-2 rounded-lg px-4 py-3`}
                        data-testid={'sent-cycles-table-header'}
                    >
                        <Text
                            weight={'bold'}
                            size={'small'}
                        >
                            {t('cycleEvaluationDashboard.sentCycles.columns.launchDate')}
                        </Text>

                        <Text
                            weight={'bold'}
                            size={'small'}
                            className={'text-center'}
                        >
                            {t('cycleEvaluationDashboard.sentCycles.columns.peopleEvaluated')}
                        </Text>

                        <Text
                            weight={'bold'}
                            size={'small'}
                            className={'text-center'}
                        >
                            {t('cycleEvaluationDashboard.sentCycles.columns.managerLaunchRate')}
                        </Text>

                        <Text
                            weight={'bold'}
                            size={'small'}
                            className={'text-center'}
                        >
                            {t('cycleEvaluationDashboard.sentCycles.columns.completionRate')}
                        </Text>

                        <Text
                            weight={'bold'}
                            size={'small'}
                            className={'text-right'}
                        >
                            {t('cycleEvaluationDashboard.sentCycles.columns.actions')}
                        </Text>
                    </div>

                    <SentCyclesContent
                        isLoading={isLoading}
                        sentCycles={sentCycles}
                        handleLaunchedSurveysClick={(sentCycleId) =>
                            openNested('launchedSurveys', sentCycleId)
                        }
                        handleManagerLaunchRateClick={(sentCycleId) =>
                            openNested('managerLaunchStatus', sentCycleId)
                        }
                    />

                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        totalCount={totalCount}
                        pageSize={PAGE_SIZE}
                        onPageChange={setCurrentPage}
                        isLoading={isLoading}
                        dataTestId={'sent-cycles-pagination'}
                        resultsSummaryText={t('common.showingResults', {
                            start: totalCount === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1,
                            end: Math.min(currentPage * PAGE_SIZE, totalCount),
                            total: totalCount,
                        })}
                    />
                </div>
            </ModalDialog>
        </>
    );
};
